import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
    RepackBySlugOrGuid,
    PaginatedRepackSamples,
} from 'apollo/repack/repack.gql';
import {
    Pack,
    Sample,
    Repack,
    RepackBySlugOrGuidQuery,
    RepackBySlugOrGuidQueryVariables,
    PaginatedRepackSamplesQuery,
    PaginatedRepackSamplesQueryVariables,
} from 'types/generated/graphql';
import { isSample } from 'types/typeGuards/isSample';

const useRepackQuery = ({
    updatePreview,
    updateSamples,
}: {
    updatePreview: (entity: Pack | Repack) => void;
    updateSamples: ({
        items,
        total,
    }: {
        items: Sample[];
        total: number;
    }) => void;
}) => {
    const [getRepack] = useLazyQuery<
        RepackBySlugOrGuidQuery,
        RepackBySlugOrGuidQueryVariables
    >(RepackBySlugOrGuid, {
        onCompleted: (data) => {
            data.repack && updatePreview(data.repack);
        },
    });

    const [getRepackSamples] = useLazyQuery<
        PaginatedRepackSamplesQuery,
        PaginatedRepackSamplesQueryVariables
    >(PaginatedRepackSamples, {
        onCompleted: (data) => {
            const samples =
                data.paginatedRepackSamples?.data?.filter(isSample) || [];

            updateSamples({
                items: samples,
                total:
                    data.paginatedRepackSamples?.meta?.totalSampleRecords ?? 0,
            });
        },
    });

    const getRepackPreview = useCallback(
        (guid: string) => {
            getRepack({
                variables: { repackSlugOrGuid: guid },
            });
            getRepackSamples({
                variables: { repackGuid: guid, take: 8 },
            });
        },
        [getRepack, getRepackSamples],
    );

    return getRepackPreview;
};

export default useRepackQuery;
